import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase, handleSupabaseError } from '../lib/supabase';
 import Button from'./ui/Button';

const Dashboard = () => {
  const { user, userProfile, signOut, profileLoading } = useAuth()
  const navigate = useNavigate()
  const [inquiries, setInquiries] = useState([])
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (user?.id) {
      loadUserData()
    }
  }, [user?.id])

  const loadUserData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Load inquiries if user is client or admin
      if (userProfile?.role === 'client' || userProfile?.role === 'admin') {
        const { data: inquiriesData, error: inquiriesError } = await supabase
          .from('legal_inquiries')
          .select(`
            *,
            legal_areas:legal_area_id(name)
          `)
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false })
        
        if (inquiriesError) throw inquiriesError
        setInquiries(inquiriesData || [])
      }
      
      // Load applications if user is lawyer
      if (userProfile?.role === 'lawyer' || userProfile?.role === 'admin') {
        const { data: applicationsData, error: applicationsError } = await supabase
          .from('lawyer_applications')
          .select(`
            *,
            legal_areas:specialization_area_id(name)
          `)
          .eq('user_id', user?.id)
          .order('submitted_at', { ascending: false })
        
        if (applicationsError) throw applicationsError
        setApplications(applicationsData || [])
      }
    } catch (error) {
      setError(handleSupabaseError(error))
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    const { error } = await signOut()
    if (!error) {
      navigate('/login')
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      assigned: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-orange-100 text-orange-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      reviewing: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getUrgencyColor = (urgency) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600', 
      high: 'text-orange-600',
      urgent: 'text-red-600'
    }
    return colors[urgency] || 'text-gray-600'
  }

  if (profileLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Dashboard wird geladen...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">
                Willkommen, {userProfile?.full_name || user?.email}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {userProfile?.role === 'admin' ? 'Administrator' : 
                 userProfile?.role === 'lawyer' ? 'Anwalt' : 'Kunde'}
              </span>
              <Button
                onClick={handleSignOut}
                variant="outline"
                size="sm"
              >
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Legal Inquiries Section */}
          {(userProfile?.role === 'client' || userProfile?.role === 'admin') && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Meine Anfragen
                </h2>
                <Button
                  onClick={() => navigate('/contact')}
                  size="sm"
                  variant="outline"
                >
                  Neue Anfrage
                </Button>
              </div>
              
              {inquiries?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Noch keine Anfragen gestellt.
                </p>
              ) : (
                <div className="space-y-4">
                  {inquiries?.slice(0, 3)?.map((inquiry) => (
                    <div key={inquiry?.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900">
                          {inquiry?.legal_areas?.name || 'Allgemeine Anfrage'}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inquiry?.status)}`}>
                          {inquiry?.status === 'new' ? 'Neu' :
                           inquiry?.status === 'assigned' ? 'Zugewiesen' :
                           inquiry?.status === 'in_progress' ? 'In Bearbeitung' :
                           inquiry?.status === 'resolved' ? 'Gelöst' : 'Geschlossen'}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <span className={`font-medium ${getUrgencyColor(inquiry?.urgency)}`}>
                          {inquiry?.urgency === 'low' ? 'Niedrige Priorität' :
                           inquiry?.urgency === 'medium' ? 'Mittlere Priorität' :
                           inquiry?.urgency === 'high' ? 'Hohe Priorität' : 'Sehr dringend'}
                        </span>
                        <span className="mx-2">•</span>
                        <span>{new Date(inquiry?.created_at)?.toLocaleDateString('de-DE')}</span>
                      </div>
                      <p className="text-gray-700 text-sm line-clamp-2">
                        {inquiry?.message}
                      </p>
                    </div>
                  ))}
                  {inquiries?.length > 3 && (
                    <div className="text-center">
                      <Button variant="outline" size="sm">
                        Alle Anfragen anzeigen ({inquiries?.length})
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Lawyer Applications Section */}
          {(userProfile?.role === 'lawyer' || userProfile?.role === 'admin') && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Meine Bewerbungen
                </h2>
                <Button
                  onClick={() => navigate('/for-lawyers')}
                  size="sm"
                  variant="outline"
                >
                  Neue Bewerbung
                </Button>
              </div>
              
              {applications?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Noch keine Bewerbungen eingereicht.
                </p>
              ) : (
                <div className="space-y-4">
                  {applications?.slice(0, 3)?.map((application) => (
                    <div key={application?.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900">
                          {application?.legal_areas?.name || 'Allgemein'}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application?.status)}`}>
                          {application?.status === 'pending' ? 'Ausstehend' :
                           application?.status === 'reviewing' ? 'Wird geprüft' :
                           application?.status === 'approved' ? 'Angenommen' : 'Abgelehnt'}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <span>{application?.years_experience} Jahre Erfahrung</span>
                        <span className="mx-2">•</span>
                        <span>{application?.location}</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(application?.submitted_at)?.toLocaleDateString('de-DE')}</span>
                      </div>
                      <p className="text-gray-700 text-sm line-clamp-2">
                        {application?.motivation}
                      </p>
                    </div>
                  ))}
                  {applications?.length > 3 && (
                    <div className="text-center">
                      <Button variant="outline" size="sm">
                        Alle Bewerbungen anzeigen ({applications?.length})
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Schnellzugriff</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => navigate('/contact')}
              variant="default"
              className="bg-trust-builder hover:bg-trust-builder/90 text-white"
            >
              Rechtliche Beratung anfragen
            </Button>
            <Button
              onClick={() => navigate('/for-lawyers')}
              variant="outline"
              className="border-conversion-accent text-conversion-accent hover:bg-conversion-accent hover:text-white"
            >
              Als Anwalt bewerben
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
            >
              Zur Hauptseite
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard